// src/pages/BallotInitiatives.js
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Circle, Users, FileText, Link, Vote, Loader, AlertTriangle, Lightbulb } from 'lucide-react';
import { fetchBallotInitiatives } from '../utils/airtable';
import './BallotInitiatives.css';

const iconStyle = { verticalAlign: 'middle' };

const POSITION_COLORS = {
  'Support': { bg: '#eaf3de', color: '#27500a', Icon: CheckCircle, label: 'Support' },
  'Oppose': { bg: '#fbeaea', color: '#791f1f', Icon: XCircle, label: 'Oppose' },
  'No Official Position': { bg: '#f0f0f0', color: '#555', Icon: Circle, label: 'No Official Position' },
};

// The Type field can come back from Airtable as a multi-select array, or as a
// single string. Some records also have multiple type values concatenated
// into one string with no delimiter (e.g. "StatutoryConstitutional
// Amendment"), so beyond comma/slash/semicolon we also split on a
// lowercase-to-uppercase letter boundary to recover the individual values.
const getTypes = (initiative) => {
  const raw = initiative.Type;
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (!raw) return [];
  return raw
    .split(/\s*[,/;]\s*|(?<=[a-z])(?=[A-Z])/)
    .map(t => t.trim())
    .filter(Boolean);
};

const InitiativeCard = ({ initiative, county }) => {
  const [expanded, setExpanded] = useState(false);
  const positionField = county?.ballotPositionField || 'LCRP Position';
  const position = POSITION_COLORS[initiative[positionField]] || POSITION_COLORS['No Official Position'];
  const levels = Array.isArray(initiative.Level)
    ? initiative.Level.join(', ')
    : initiative.Level || '';
  const types = getTypes(initiative).join(', ');

  return (
    <div className="initiative-card">
      <div className="initiative-header" onClick={() => setExpanded(!expanded)}>
        <div className="initiative-header-left">
  <div className="initiative-title">{initiative.Name}</div>
  <div className="initiative-badges">
    {initiative['Initiative Number'] && (
      <span className="initiative-number">{initiative['Initiative Number']}</span>
    )}
    {initiative['Amendment/Proposition Number'] && (
      <span className="initiative-amendment-number">{initiative['Amendment/Proposition Number']}</span>
    )}
    {levels && (
      <span className="initiative-level">{levels}</span>
    )}
    {types && (
      <span className="initiative-type">{types}</span>
    )}
  </div>
  {initiative['Ballot Title'] && (
    <div className="initiative-ballot-title">"{initiative['Ballot Title']}"</div>
  )}
</div>
        <div className="initiative-header-right">
          <div
            className="position-badge"
            style={{ background: position.bg, color: position.color }}
          >
            <position.Icon size={16} color="currentColor" style={iconStyle} /> {position.label}
          </div>
          <div className="expand-icon">{expanded ? '▲' : '▼'}</div>
        </div>
      </div>

      {expanded && (
        <div className="initiative-body">
          {initiative.Supporters && (
            <div className="initiative-section">
              <div className="initiative-section-label"><Users size={16} color="currentColor" style={iconStyle} /> Supporters</div>
              <div className="initiative-section-content">{initiative.Supporters}</div>
            </div>
          )}

          {initiative.Opponents && (
            <div className="initiative-section">
              <div className="initiative-section-label"><Users size={16} color="currentColor" style={iconStyle} /> Opponents</div>
              <div className="initiative-section-content">{initiative.Opponents}</div>
            </div>
          )}

          <div className="initiative-links">
            {initiative['Full Text'] && (
              <a className="initiative-link red" href={initiative['Full Text']} target="_blank" rel="noreferrer">
                <FileText size={16} color="currentColor" style={iconStyle} /> Read full text
              </a>
            )}
            {initiative['Learn More'] && (
              <a className="initiative-link" href={initiative['Learn More']} target="_blank" rel="noreferrer">
                <Link size={16} color="currentColor" style={iconStyle} /> Learn more
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const BallotInitiatives = ({ county }) => {
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Ballot initiatives are only ever on the General election ballot
  const activeElection = 'General 2026';
  const [activeScope, setActiveScope] = useState('All');
  const [activeType, setActiveType] = useState('All Types');

  const SCOPES = ['All', 'Statewide', 'Local'];

  useEffect(() => {
    fetchBallotInitiatives()
      .then(data => { setInitiatives(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  const getLevels = (initiative) => Array.isArray(initiative.Level)
    ? initiative.Level
    : (initiative.Level ? [initiative.Level] : []);

  const typeOptions = ['All Types', ...Array.from(new Set(
    initiatives.filter(i => i.Election === activeElection).flatMap(getTypes)
  ))];

  const filtered = initiatives.filter(i => {
    const electionMatch = i.Election === activeElection;
    const levels = getLevels(i);
    const scopeMatch = activeScope === 'All'
      || (activeScope === 'Statewide' && levels.includes('Statewide'))
      || (activeScope === 'Local' && levels.some(l => l !== 'Statewide'));
    const typeMatch = activeType === 'All Types' || getTypes(i).includes(activeType);
    return electionMatch && scopeMatch && typeMatch;
  });

  const totalForElection = initiatives.filter(i => i.Election === activeElection).length;

  return (
    <div>
      <div className="sec-head">
        <div className="sec-icon"><Vote size={20} color="currentColor" style={iconStyle} /></div>
        <div>
          <div className="sec-title">Ballot initiatives</div>
          <div className="sec-desc">2026 ballot measures and the {county?.orgName || 'Larimer County Republican Party'} position</div>
        </div>
      </div>

      <div className="pills" style={{ marginBottom: 10 }}>
        {SCOPES.map(scope => (
          <span
            key={scope}
            className={`pill ${activeScope === scope ? 'on' : ''}`}
            onClick={() => setActiveScope(scope)}
          >
            {scope}
          </span>
        ))}
      </div>

      <div className="pills" style={{ marginBottom: 12 }}>
        {typeOptions.map(type => (
          <span
            key={type}
            className={`pill ${activeType === type ? 'on' : ''}`}
            onClick={() => setActiveType(type)}
          >
            {type}
          </span>
        ))}
      </div>

      {loading && <div className="loading"><Loader size={16} color="currentColor" style={iconStyle} /> Loading ballot initiatives...</div>}

      {error && (
        <div className="error-msg">
          <AlertTriangle size={16} color="currentColor" style={iconStyle} /> Could not load ballot initiatives. Please check your Airtable connection.
        </div>
      )}

      {!loading && !error && totalForElection > 0 && (
        <div style={{ fontSize: 12, color: '#999', marginBottom: 10 }}>
          Showing {filtered.length} of {totalForElection} initiatives
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div style={{ background: '#f5f5f5', borderRadius: 6, padding: 16, fontSize: 13, color: '#666', lineHeight: 1.6 }}>
          No ballot initiatives found for {activeElection}
          {activeScope !== 'All' ? ` · ${activeScope}` : ''}
          {activeType !== 'All Types' ? ` · ${activeType}` : ''}. Check back as measures are certified for the ballot.
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="initiatives-list">
          {filtered.map(initiative => (
            <InitiativeCard key={initiative.id} initiative={initiative} county={county} />
          ))}
        </div>
      )}

      <div style={{ marginTop: 16, fontSize: 12, color: '#999', lineHeight: 1.6 }}>
        <Lightbulb size={16} color="currentColor" style={iconStyle} /> Positions reflect the official stance of the {county?.orgName || 'Larimer County Republican Party'}. Full ballot text available at the <a href="https://www.sos.state.co.us" target="_blank" rel="noreferrer" style={{ color: '#999' }}>Colorado Secretary of State</a>.
      </div>
    </div>
  );
};

export default BallotInitiatives;
